import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { SafeAreaView } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/MaterialIcons';

import { AppDispatch, RootState } from '../../../store';
import {
  getTutoringAnalytics,
  getPracticeRecommendations,
  selectTutoringAnalytics,
  selectPracticeRecommendations,
  selectTutoringLoading,
  selectTutoringError,
} from '../../../store/slices/aiTutoringSlice';
import { selectCurrentUser } from '../../../store/slices/authSlice';

import TutoringOverviewCard from '../../../components/ai/tutoring/TutoringOverviewCard';
import AdaptiveExplanationCard from '../../../components/ai/tutoring/AdaptiveExplanationCard';
import ContextualHelpOverlay from '../../../components/ai/tutoring/ContextualHelpOverlay';
import PracticeRecommendationsList from '../../../components/ai/tutoring/PracticeRecommendationsList';
import QuizGenerationCard from '../../../components/ai/tutoring/QuizGenerationCard';
import TutoringEffectivenessChart from '../../../components/ai/tutoring/TutoringEffectivenessChart';
import TutorPersonalitySelector from '../../../components/ai/tutoring/TutorPersonalitySelector';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../../components/common/ErrorMessage';
import { logger } from '../../../utils/logger';

const AITutoringDashboardScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectCurrentUser);
  const analytics = useSelector(selectTutoringAnalytics);
  const practiceRecommendations = useSelector(selectPracticeRecommendations);
  const loading = useSelector(selectTutoringLoading);
  const error = useSelector(selectTutoringError);

  const [selectedTab, setSelectedTab] = useState<
    'overview' | 'practice' | 'analytics'
  >('overview');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadTutoringData();
    }
  }, [user?.id]);

  const loadTutoringData = async () => {
    if (!user?.id) return;

    try {
      await Promise.all([
        dispatch(
          getTutoringAnalytics({ userId: user.id, timeframe: 'weekly' })
        ),
        dispatch(getPracticeRecommendations(user.id)),
      ]);
    } catch (err) {
      logger.error('Failed to load tutoring data:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTutoringData();
    setRefreshing(false);
  };

  const handleStartTutoringSession = (topicId: string, sessionType: any) => {
    // Navigate to tutoring session screen
    Alert.alert(
      'Starting Tutoring Session',
      `Topic: ${topicId}, Type: ${sessionType}`
    );
  };

  const handleGenerateQuiz = (topicId: string) => {
    // Navigate to quiz generation screen
    Alert.alert('Generating Quiz', `Topic: ${topicId}`);
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'overview':
        return (
          <View style={styles.tabContent}>
            <TutoringOverviewCard
              analytics={analytics}
              onStartSession={handleStartTutoringSession}
              loading={loading.analytics}
            />

            <AdaptiveExplanationCard
              onRequestExplanation={topicId =>
                handleStartTutoringSession(topicId, 'explanation')
              }
              loading={loading.explanation}
            />

            <QuizGenerationCard
              onGenerateQuiz={handleGenerateQuiz}
              loading={loading.quiz}
            />
          </View>
        );

      case 'practice':
        return (
          <View style={styles.tabContent}>
            <PracticeRecommendationsList
              recommendations={practiceRecommendations}
              onStartPractice={recommendation =>
                handleStartTutoringSession(
                  recommendation.recommendationId,
                  'practice'
                )
              }
              loading={loading.practice}
            />
          </View>
        );

      case 'analytics':
        return (
          <View style={styles.tabContent}>
            <TutoringEffectivenessChart
              analytics={analytics}
              loading={loading.analytics}
            />
          </View>
        );

      default:
        return null;
    }
  };

  if (loading.analytics && !analytics) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <LoadingSpinner message='Loading AI tutoring dashboard...' />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>AI Tutoring</Text>
          <Text style={styles.headerSubtitle}>
            Personalized learning assistance
          </Text>
        </View>

        <TouchableOpacity
          style={styles.refreshButton}
          onPress={handleRefresh}
          disabled={refreshing}
        >
          <Icon
            name='refresh'
            size={24}
            color='#007AFF'
            style={refreshing ? styles.spinning : undefined}
          />
        </TouchableOpacity>
      </View>

      {/* Tutor Personality Selector */}
      <View style={styles.personalitySection}>
        <TutorPersonalitySelector />
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabNavigation}>
        {[
          { key: 'overview', label: 'Overview', icon: 'dashboard' },
          { key: 'practice', label: 'Practice', icon: 'fitness-center' },
          { key: 'analytics', label: 'Analytics', icon: 'analytics' },
        ].map(tab => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tabButton,
              selectedTab === tab.key && styles.activeTabButton,
            ]}
            onPress={() => setSelectedTab(tab.key as any)}
          >
            <Icon
              name={tab.icon}
              size={20}
              color={selectedTab === tab.key ? '#007AFF' : '#666'}
            />
            <Text
              style={[
                styles.tabLabel,
                selectedTab === tab.key && styles.activeTabLabel,
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Error Display */}
      {error && (
        <ErrorMessage
          message={error}
          onRetry={handleRefresh}
          style={styles.errorMessage}
        />
      )}

      {/* Tab Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshing={refreshing}
        onRefresh={handleRefresh}
      >
        {renderTabContent()}
      </ScrollView>

      {/* Contextual Help Overlay */}
      <ContextualHelpOverlay />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  refreshButton: {
    padding: 8,
  },
  spinning: {
    transform: [{ rotate: '180deg' }],
  },
  personalitySection: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  tabNavigation: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  activeTabButton: {
    backgroundColor: '#f0f8ff',
  },
  tabLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginLeft: 6,
  },
  activeTabLabel: {
    color: '#007AFF',
    fontWeight: '600',
  },
  errorMessage: {
    margin: 20,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  tabContent: {
    gap: 16,
  },
});

export default AITutoringDashboardScreen;
